/**
 * UsersAdminPanel — collapsible section in /admin to inspect and manage
 * registered users.
 *
 * Features:
 *  - Stats summary (totale + breakdown per premium_status)
 *  - Search by email substring
 *  - Filter by status (Free/Trialing/Active/Cancelled/Past Due/Lifetime/Grandfathered)
 *  - Per-user detail drawer with audit history + last transactions
 *  - Grant Premium (manual, ex. Sportium 60 days)
 *  - Revoke Premium (with reason, tracked in audit)
 *  - Healthcheck Stripe (one-click self-test)
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  Users, Search, ChevronDown, ChevronRight, Loader2, X,
  ShieldCheck, ShieldAlert, Sparkles, Crown, AlertTriangle,
  Activity, Gift, Calendar, Mail, RefreshCw, CheckCircle2, XCircle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const C = {
  surface: "var(--bc-panel, #09090B)",
  surface2: "var(--bc-panel-elev, #141416)",
  border: "var(--bc-border, rgba(255,255,255,0.08))",
  text: "var(--bc-ink, #FFFFFF)",
  muted: "var(--bc-sub, #71717A)",
  green: "#22FFA3",
  red: "#EF4444",
  cyan: "#06B6D4",
  yellow: "#F59E0B",
  violet: "#A78BFA",
  accent: "#C8FF00",
};

const STATUS_BADGE = {
  none:          { label: "Free",       color: C.muted },
  trialing:      { label: "Trial",      color: C.cyan },
  active:        { label: "Active",     color: C.green },
  past_due:      { label: "Past Due",   color: C.yellow },
  cancelled:     { label: "Cancelled",  color: C.red },
  lifetime:      { label: "Lifetime",   color: C.violet },
  grandfathered: { label: "Grandfathered", color: C.green },
};

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("it-IT", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function StatusBadge({ status }) {
  const meta = STATUS_BADGE[status] || STATUS_BADGE.none;
  return (
    <span style={{
      padding: "0.18rem 0.5rem",
      border: `1px solid ${meta.color}55`,
      background: `${meta.color}10`,
      color: meta.color,
      fontFamily: "'JetBrains Mono',monospace",
      fontSize: "0.62rem",
      letterSpacing: "0.10em",
      textTransform: "uppercase",
      fontWeight: 700,
    }}>{meta.label}</span>
  );
}

export default function UsersAdminPanel({ token, onToast }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [healthcheck, setHealthcheck] = useState(null);
  const [hcBusy, setHcBusy] = useState(false);

  const load = useCallback(async () => {
    if (!token || !open) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ admin_token: token, limit: "200" });
      if (q) params.set("q", q);
      if (filter) params.set("status", filter);
      const res = await axios.get(`${API}/admin/users?${params.toString()}`);
      setData(res.data);
    } catch (e) {
      onToast?.(`Errore caricamento utenti: ${e.message}`, "error");
    } finally {
      setLoading(false);
    }
  }, [token, open, q, filter, onToast]);

  useEffect(() => { load(); }, [load]);

  const runHealthcheck = async () => {
    setHcBusy(true);
    try {
      const res = await axios.get(`${API}/admin/billing-healthcheck?admin_token=${token}`);
      setHealthcheck(res.data);
    } catch (e) {
      onToast?.(`Errore healthcheck: ${e.message}`, "error");
    } finally {
      setHcBusy(false);
    }
  };

  const handleGrant = async (email, source, durationDays) => {
    try {
      await axios.post(`${API}/admin/grant-premium?admin_token=${token}`,
        { email, source, duration_days: durationDays });
      onToast?.(`Premium attivato per ${email}`, "success");
      load();
      setSelected(null);
    } catch (e) {
      onToast?.(e.response?.data?.detail || e.message, "error");
    }
  };

  const handleRevoke = async (email) => {
    const reason = prompt("Motivo della revoca:");
    if (!reason) return;
    try {
      await axios.post(`${API}/admin/revoke-premium?admin_token=${token}`,
        { email, reason });
      onToast?.(`Premium revocato per ${email}`, "success");
      load();
      setSelected(null);
    } catch (e) {
      onToast?.(e.response?.data?.detail || e.message, "error");
    }
  };

  const [syncBusy, setSyncBusy] = useState(false);
  const [bulkSyncBusy, setBulkSyncBusy] = useState(false);

  const handleSyncUser = async (email) => {
    setSyncBusy(true);
    try {
      const res = await axios.post(`${API}/admin/sync-user-from-stripe?admin_token=${token}`, { email });
      const status = res.data?.status;
      if (status === "ok") {
        const newStatus = res.data?.after?.premium_status || "—";
        onToast?.(`Sync OK · ${email} ora è ${newStatus}`, "success");
      } else if (status === "not_found_on_stripe") {
        onToast?.(`Nessun customer Stripe trovato per ${email}`, "error");
      } else if (status === "no_subscriptions") {
        onToast?.(`Customer trovato ma nessuna subscription da applicare`, "error");
      } else {
        onToast?.(`Sync completato (${status})`, "success");
      }
      load();
      setSelected(null);
    } catch (e) {
      onToast?.(e.response?.data?.detail || e.message, "error");
    } finally {
      setSyncBusy(false);
    }
  };

  const handleResetTrial = async (email) => {
    const reason = prompt("Motivo del reset del trial (es. 'rimborso amichevole'):");
    if (!reason) return;
    try {
      await axios.post(`${API}/admin/reset-trial?admin_token=${token}`, { email, reason });
      onToast?.(`Trial reset per ${email} · ora può riattivare la prova`, "success");
      load();
      setSelected(null);
    } catch (e) {
      onToast?.(e.response?.data?.detail || e.message, "error");
    }
  };

  const handleBulkSync = async () => {
    if (!confirm("Riconcilia da Stripe TUTTE le subscriptions create nelle ultime 72h? Operazione sicura e idempotente.")) return;
    setBulkSyncBusy(true);
    try {
      const res = await axios.post(`${API}/admin/sync-all-recent-from-stripe?hours=72&admin_token=${token}`);
      const fixed = res.data?.fixed_count || 0;
      const scanned = res.data?.scanned || 0;
      onToast?.(`Bulk sync · ${scanned} subs viste · ${fixed} utenti aggiornati`, "success");
      load();
    } catch (e) {
      onToast?.(e.response?.data?.detail || e.message, "error");
    } finally {
      setBulkSyncBusy(false);
    }
  };

  return (
    <div data-testid="users-admin-panel" style={{
      background: C.surface, border: `1px solid ${C.border}`, marginBottom: "1.5rem",
    }}>
      {/* Header / toggle */}
      <button
        type="button"
        data-testid="users-admin-toggle"
        onClick={() => setOpen(!open)}
        style={{
          width: "100%", padding: "1rem 1.25rem",
          background: "transparent", border: "none", color: C.text,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          cursor: "pointer",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <Users size={15} style={{ color: C.cyan }} />
          <span style={{
            fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
            fontSize: "0.95rem", letterSpacing: "0.16em", textTransform: "uppercase",
          }}>
            Utenti registrati & Premium
          </span>
        </span>
        {data?.stats && (
          <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "0.72rem", color: C.muted }}>
            {data.stats.total_users} utenti totali
          </span>
        )}
      </button>

      {!open ? null : (
        <div style={{ borderTop: `1px solid ${C.border}`, padding: "1.25rem" }}>
          {/* Stats chips */}
          {data?.stats?.by_status && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: "1rem" }}>
              {Object.entries({
                "active": data.stats.by_status.active || 0,
                "trialing": data.stats.by_status.trialing || 0,
                "lifetime": data.stats.by_status.lifetime || 0,
                "grandfathered": data.stats.by_status.grandfathered || 0,
                "past_due": data.stats.by_status.past_due || 0,
                "cancelled": data.stats.by_status.cancelled || 0,
                "none": (data.stats.by_status.none || 0) + (data.stats.by_status[""] || 0),
              }).map(([k, n]) => {
                const meta = STATUS_BADGE[k] || STATUS_BADGE.none;
                return (
                  <div key={k} style={{
                    padding: "0.45rem 0.7rem",
                    background: `${meta.color}10`,
                    border: `1px solid ${meta.color}55`,
                    color: meta.color,
                    fontFamily: "'JetBrains Mono',monospace",
                    fontSize: "0.72rem",
                  }}>
                    <strong style={{ fontSize: "0.95rem" }}>{n}</strong> {meta.label}
                  </div>
                );
              })}
            </div>
          )}

          {/* Search + filter + healthcheck */}
          <div style={{ display: "flex", gap: 8, marginBottom: "1rem", flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 280px", position: "relative" }}>
              <Search size={12} style={{ position: "absolute", top: 10, left: 10, color: C.muted }} />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Cerca email o @telegram..."
                data-testid="users-admin-search"
                style={{
                  width: "100%", padding: "0.55rem 0.5rem 0.55rem 2rem",
                  background: C.surface2, border: `1px solid ${C.border}`,
                  color: C.text, fontFamily: "'JetBrains Mono',monospace", fontSize: "0.75rem",
                  outline: "none",
                }}
              />
            </div>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              data-testid="users-admin-filter"
              style={{
                padding: "0.55rem 0.7rem",
                background: C.surface2, border: `1px solid ${C.border}`,
                color: C.text, fontFamily: "'JetBrains Mono',monospace", fontSize: "0.72rem",
                cursor: "pointer",
              }}>
              <option value="">Tutti</option>
              <option value="free">Free</option>
              <option value="trialing">Trialing</option>
              <option value="active">Active</option>
              <option value="past_due">Past due</option>
              <option value="cancelled">Cancelled</option>
              <option value="lifetime">Lifetime</option>
              <option value="grandfathered">Grandfathered</option>
            </select>
            <button
              type="button"
              onClick={load}
              data-testid="users-admin-refresh"
              style={{
                padding: "0.55rem 0.85rem",
                background: "transparent", border: `1px solid ${C.border}`,
                color: C.text, cursor: "pointer",
                fontFamily: "'JetBrains Mono',monospace", fontSize: "0.7rem",
                display: "inline-flex", alignItems: "center", gap: 6,
              }}>
              <RefreshCw size={11} /> Aggiorna
            </button>
            <button
              type="button"
              onClick={runHealthcheck}
              disabled={hcBusy}
              data-testid="users-admin-healthcheck"
              style={{
                padding: "0.55rem 0.85rem",
                background: "rgba(34,255,163,0.08)", border: `1px solid ${C.green}55`,
                color: C.green, cursor: hcBusy ? "wait" : "pointer",
                fontFamily: "'JetBrains Mono',monospace", fontSize: "0.7rem",
                display: "inline-flex", alignItems: "center", gap: 6,
              }}>
              {hcBusy ? <Loader2 size={11} className="animate-spin" /> : <Activity size={11} />}
              Stripe healthcheck
            </button>
            <button
              type="button"
              onClick={handleBulkSync}
              disabled={bulkSyncBusy}
              data-testid="users-admin-bulk-sync"
              title="Riconcilia con Stripe tutte le subscriptions create nelle ultime 72h"
              style={{
                padding: "0.55rem 0.85rem",
                background: "rgba(6,182,212,0.10)", border: `1px solid ${C.cyan}55`,
                color: C.cyan, cursor: bulkSyncBusy ? "wait" : "pointer",
                fontFamily: "'JetBrains Mono',monospace", fontSize: "0.7rem",
                display: "inline-flex", alignItems: "center", gap: 6,
              }}>
              {bulkSyncBusy ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
              Bulk sync 72h
            </button>
          </div>

          {/* Healthcheck results */}
          {healthcheck && (
            <div data-testid="users-admin-healthcheck-results" style={{
              marginBottom: "1rem", padding: "0.7rem 0.85rem",
              background: C.surface2, border: `1px solid ${C.border}`,
              fontFamily: "'JetBrains Mono',monospace", fontSize: "0.72rem",
            }}>
              <div style={{ marginBottom: 6, color: C.text, fontWeight: 700 }}>
                Healthcheck: <span style={{ color: C.green }}>{healthcheck.summary.ok} OK</span> ·{" "}
                <span style={{ color: C.red }}>{healthcheck.summary.fail} FAIL</span> ·{" "}
                <span style={{ color: C.muted }}>{healthcheck.summary.skip} SKIP</span>
              </div>
              {healthcheck.checks.map((c, i) => (
                <div key={i} style={{
                  padding: "0.2rem 0",
                  color: c.status === "ok" ? C.green : c.status === "fail" ? C.red : C.muted,
                }}>
                  {c.status === "ok" ? "✓" : c.status === "fail" ? "✗" : "○"} {c.name} — {c.detail}
                </div>
              ))}
            </div>
          )}

          {/* User table */}
          {loading ? (
            <div style={{ padding: "2rem", textAlign: "center", color: C.muted }}>
              <Loader2 size={20} className="animate-spin" style={{ display: "inline-block" }} />
            </div>
          ) : !data?.users?.length ? (
            <div style={{ padding: "2rem", textAlign: "center", color: C.muted, fontFamily: "'JetBrains Mono',monospace", fontSize: "0.75rem" }}>
              Nessun utente trovato.
            </div>
          ) : (
            <div style={{ overflowX: "auto", marginTop: 4 }}>
              <table data-testid="users-admin-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.75rem" }}>
                <thead>
                  <tr style={{ color: C.muted, fontFamily: "'JetBrains Mono',monospace", fontSize: "0.62rem", letterSpacing: "0.10em", textTransform: "uppercase" }}>
                    <th style={th()}>Email</th>
                    <th style={th()}>Status</th>
                    <th style={th()}>Source</th>
                    <th style={th()}>Renews / Expires</th>
                    <th style={th()}>Creato</th>
                    <th style={th()}>Ultimo login</th>
                    <th style={th()}></th>
                  </tr>
                </thead>
                <tbody>
                  {data.users.map((u) => (
                    <tr key={u.id} style={{ borderTop: `1px solid ${C.border}` }} data-testid={`user-row-${u.email}`}>
                      <td style={td()}>
                        <div>{u.email}</div>
                        {u.telegram_username ? (
                          <a
                            href={`https://t.me/${(u.telegram_username || "").replace(/^@/, "")}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`user-telegram-${u.email}`}
                            style={{
                              display: "inline-block",
                              marginTop: 3,
                              color: "#06B6D4",
                              fontFamily: "'JetBrains Mono',monospace",
                              fontSize: "0.62rem",
                              letterSpacing: "0.04em",
                              textDecoration: "none",
                            }}
                            title="Apri chat Telegram"
                          >
                            ✈ {u.telegram_username}
                          </a>
                        ) : (
                          <div style={{
                            color: C.muted,
                            fontFamily: "'JetBrains Mono',monospace",
                            fontSize: "0.6rem",
                            marginTop: 3,
                            opacity: 0.5,
                          }}>
                            no telegram
                          </div>
                        )}
                      </td>
                      <td style={td()}><StatusBadge status={u.premium_status} /></td>
                      <td style={{ ...td(), color: C.muted, fontFamily: "'JetBrains Mono',monospace", fontSize: "0.7rem" }}>
                        {u.premium_source || "—"}
                      </td>
                      <td style={{ ...td(), color: C.muted, fontFamily: "'JetBrains Mono',monospace", fontSize: "0.7rem" }}>
                        {formatDate(u.stripe_current_period_end)}
                      </td>
                      <td style={{ ...td(), color: C.muted, fontFamily: "'JetBrains Mono',monospace", fontSize: "0.7rem" }}>
                        {formatDate(u.created_at)}
                      </td>
                      <td style={{ ...td(), color: C.muted, fontFamily: "'JetBrains Mono',monospace", fontSize: "0.7rem" }}>
                        {formatDate(u.last_login_at)}
                      </td>
                      <td style={td()}>
                        <button onClick={() => setSelected(u)} style={{
                          padding: "0.3rem 0.6rem", background: "transparent",
                          border: `1px solid ${C.border}`, color: C.text,
                          cursor: "pointer", fontSize: "0.66rem",
                        }} data-testid={`user-row-action-${u.email}`}>
                          Gestisci →
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* User detail drawer */}
      {selected && (
        <UserDetailDrawer
          user={selected}
          token={token}
          onClose={() => setSelected(null)}
          onGrant={handleGrant}
          onRevoke={handleRevoke}
          onSync={handleSyncUser}
          onResetTrial={handleResetTrial}
          syncBusy={syncBusy}
        />
      )}
    </div>
  );
}

const th = () => ({ textAlign: "left", padding: "0.5rem 0.5rem", fontWeight: 600 });
const td = () => ({ padding: "0.55rem 0.5rem", color: "var(--bc-ink, #FFFFFF)" });

// ────────── DRAWER ──────────

function UserDetailDrawer({ user, token, onClose, onGrant, onRevoke, onSync, onResetTrial, syncBusy }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/users/${user.id}?admin_token=${token}`)
      .then((res) => setDetail(res.data))
      .catch(() => setDetail(null))
      .finally(() => setLoading(false));
  }, [user.id, token]);

  const u = detail?.user || user;

  return (
    <div
      role="dialog"
      data-testid="user-detail-drawer"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 90,
        background: "rgba(0,0,0,0.6)", backdropFilter: "blur(2px)",
        display: "flex", justifyContent: "flex-end",
      }}
    >
      <div onClick={(e) => e.stopPropagation()} style={{
        width: "min(560px, 96vw)", height: "100%", overflow: "auto",
        background: C.surface, borderLeft: `1px solid ${C.border}`,
        padding: "1.5rem", color: C.text,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
          <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 800, fontSize: "1.05rem", letterSpacing: "0.16em", textTransform: "uppercase" }}>
            Dettaglio utente
          </div>
          <button onClick={onClose} style={{ background: "transparent", border: "none", color: C.muted, cursor: "pointer" }} data-testid="user-detail-close">
            <X size={16} />
          </button>
        </div>

        {/* Identity card */}
        <div style={{ padding: "0.9rem", background: C.surface2, border: `1px solid ${C.border}`, marginBottom: "1rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <Mail size={13} style={{ color: C.cyan }} />
            <span style={{ fontWeight: 600, wordBreak: "break-all" }}>{u.email}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 6 }}>
            <StatusBadge status={u.premium_status} />
            {u.is_lifetime && <Crown size={13} style={{ color: C.violet }} />}
            <span style={{ color: C.muted, fontSize: "0.72rem", fontFamily: "'JetBrains Mono',monospace" }}>
              source: {u.premium_source || "—"}
            </span>
          </div>
          {u.stripe_current_period_end && (
            <div style={{ color: C.muted, fontSize: "0.72rem", fontFamily: "'JetBrains Mono',monospace" }}>
              <Calendar size={10} style={{ display: "inline" }} /> Rinnovo: {formatDate(u.stripe_current_period_end)}
            </div>
          )}
          {u.claimed_access_code && (
            <div style={{ color: C.green, fontSize: "0.72rem", fontFamily: "'JetBrains Mono',monospace" }}>
              Codice legacy: <strong>{u.claimed_access_code}</strong>
            </div>
          )}
          {u.past_due_since && (
            <div style={{ color: C.yellow, fontSize: "0.72rem", fontFamily: "'JetBrains Mono',monospace" }}>
              <AlertTriangle size={10} style={{ display: "inline" }} /> Past_due dal: {formatDate(u.past_due_since)}
            </div>
          )}
          {u.trial_used && (
            <div style={{ color: C.yellow, fontSize: "0.72rem", fontFamily: "'JetBrains Mono',monospace", marginTop: 4 }}>
              ⚠ Trial già usato {u.trial_used_at ? `il ${formatDate(u.trial_used_at)}` : ""} — niente prova gratuita su nuovi checkout
              {u.trial_blocked_reason === "duplicate_card_fingerprint" && (
                <div style={{ color: C.red, marginTop: 2 }}>
                  🚫 Bloccato per carta duplicata (fingerprint match con altro account)
                </div>
              )}
            </div>
          )}
        </div>

        {/* Grant actions */}
        <div style={{ padding: "0.9rem", background: C.surface2, border: `1px solid ${C.border}`, marginBottom: "1rem" }}>
          <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, fontSize: "0.85rem", letterSpacing: "0.14em", textTransform: "uppercase", color: C.green, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
            <Gift size={13} /> Azioni Premium
          </div>
          <div style={{ display: "grid", gap: 6, gridTemplateColumns: "repeat(auto-fit, minmax(140px,1fr))" }}>
            <button onClick={() => onGrant(u.email, "sportium", 60)} data-testid="grant-sportium-btn" style={btnAction(C.red)}>
              <Sparkles size={11} /> Sportium · 60gg
            </button>
            <button onClick={() => onGrant(u.email, "promo", 30)} data-testid="grant-promo-btn" style={btnAction(C.cyan)}>
              <Gift size={11} /> Promo · 30gg
            </button>
            <button onClick={() => onGrant(u.email, "support", null)} data-testid="grant-lifetime-btn" style={btnAction(C.violet)}>
              <Crown size={11} /> Lifetime
            </button>
            <button onClick={() => onRevoke(u.email)} data-testid="revoke-btn" style={btnAction(C.red, true)}>
              <XCircle size={11} /> Revoca
            </button>
          </div>
        </div>

        {/* Audit history */}
        {detail?.audit?.length > 0 && (
          <div style={{ marginBottom: "1rem" }}>
            <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, fontSize: "0.85rem", letterSpacing: "0.14em", textTransform: "uppercase", color: C.text, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
              <Activity size={13} /> Audit history ({detail.audit.length})
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 280, overflow: "auto" }}>
              {detail.audit.map((a, i) => (
                <div key={i} style={{
                  padding: "0.5rem 0.7rem", background: C.surface2, border: `1px solid ${C.border}`,
                  fontFamily: "'JetBrains Mono',monospace", fontSize: "0.66rem",
                }}>
                  <div style={{ color: C.text, marginBottom: 2 }}>
                    <strong style={{ color: C.cyan }}>{a.source}</strong> · {a.triggered_by}
                  </div>
                  <div style={{ color: C.muted }}>{formatDate(a.at)}</div>
                  {a.delta && Object.keys(a.delta).length > 0 && (
                    <div style={{ marginTop: 3, color: C.muted, whiteSpace: "pre-wrap" }}>
                      {Object.entries(a.delta).map(([k, v]) => (
                        <div key={k}>↳ {k}: {String(v.before ?? "∅")} → <strong style={{ color: C.green }}>{String(v.after ?? "∅")}</strong></div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Transactions */}
        {detail?.transactions?.length > 0 && (
          <div>
            <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, fontSize: "0.85rem", letterSpacing: "0.14em", textTransform: "uppercase", color: C.text, marginBottom: 8 }}>
              Transazioni Stripe ({detail.transactions.length})
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {detail.transactions.map((t, i) => (
                <div key={i} style={{
                  padding: "0.5rem 0.7rem", background: C.surface2, border: `1px solid ${C.border}`,
                  fontFamily: "'JetBrains Mono',monospace", fontSize: "0.66rem", color: C.muted,
                }}>
                  <div style={{ color: C.text }}>
                    <strong>{t.plan}</strong> · {t.status} · {t.payment_status || "—"}
                    {t.via && <span style={{ color: C.cyan, marginLeft: 6 }}>via {t.via}</span>}
                  </div>
                  {t.amount_total != null && (
                    <div>€{(t.amount_total / 100).toFixed(2)} {(t.currency || "eur").toUpperCase()}</div>
                  )}
                  <div>{formatDate(t.created_at)}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {loading && (
          <div style={{ textAlign: "center", padding: "1rem", color: C.muted }}>
            <Loader2 size={16} className="animate-spin" style={{ display: "inline-block" }} />
          </div>
        )}
      </div>
    </div>
  );
}

const btnAction = (color, danger = false) => ({
  padding: "0.5rem 0.6rem",
  background: danger ? `${color}15` : `${color}10`,
  border: `1px solid ${color}55`,
  color,
  cursor: "pointer",
  fontFamily: "'JetBrains Mono',monospace",
  fontSize: "0.66rem",
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  display: "inline-flex", alignItems: "center", gap: 5, justifyContent: "center",
  transition: "background 0.15s",
});
